import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { projectService, personName, Project, ProjectTask, ProjectUser } from '../../services/projectService';
import { sanitizeHtml, isEmptyHtml } from '../../utils/sanitizeHtml';
import { getAssetUrl } from '../../utils/assets';
import { useAuth } from '../../context/AuthContext';
import CommentRichEditor from './CommentRichEditor';

const COMPLETION_OPTIONS = Array.from({ length: 11 }, (_, i) => i * 10);

const insertMention = (text: string, user: ProjectUser) => {
  const mention = `<span data-user-id="${user._id}">@${personName(user)}</span> `;
  return `${text}${mention}`;
};

const parseDay = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const toIsoDay = (value: Date | null) => {
  if (!value) return null;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatFileSize = (n?: number) => {
  const size = Number(n) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const initials = (user?: ProjectUser | null) => {
  const letters = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.trim();
  return (letters || user?.email?.[0] || '?').toUpperCase();
};

const participantIdsOf = (task: ProjectTask | null) => {
  const ids: string[] = [];
  (task?.participants || []).forEach((person) => {
    if (person._id && !ids.includes(person._id)) ids.push(person._id);
  });
  const assignedId = task?.assignedTo?._id;
  if (assignedId && !ids.includes(assignedId)) ids.unshift(assignedId);
  else if (assignedId) {
    ids.splice(ids.indexOf(assignedId), 1);
    ids.unshift(assignedId);
  }
  return ids;
};

const finishedColumnIdOf = (project: Project) => {
  if (project.finishedColumnId) return project.finishedColumnId;
  const byId = project.columns.find((column) => column.id === 'done');
  if (byId) return byId.id;
  const byTitle = project.columns.find((column) => column.title.toLowerCase().trim() === 'done');
  return byTitle?.id || 'done';
};

interface Props {
  project: Project;
  task: ProjectTask | null;
  users: ProjectUser[];
  tasks: ProjectTask[];
  onClose: () => void;
  onSaved: (task: ProjectTask) => void;
  onDeleted: (taskId: string) => void;
  focusCommentId?: string;
  initialStatus?: string;
}

const PRIORITY_OPTIONS = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' }
];

const ProjectTaskModal: React.FC<Props> = ({
  project,
  task,
  tasks,
  onClose,
  onSaved,
  onDeleted,
  focusCommentId,
  initialStatus
}) => {
  const { user } = useAuth();
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [priority, setPriority] = useState(task?.priority || 'medium');
  const [status, setStatus] = useState(task?.status || initialStatus || project.columns[0]?.id || 'todo');
  const [completionPercent, setCompletionPercent] = useState(task?.completionPercent || 0);
  const [dueDate, setDueDate] = useState<Date | null>(parseDay(task?.dueDate));
  const [participants, setParticipants] = useState<string[]>(participantIdsOf(task));
  const [dependsOn, setDependsOn] = useState<string[]>(
    (task?.dependsOn || []).map((dep) => (typeof dep === 'string' ? dep : dep._id))
  );
  const [comment, setComment] = useState('');
  const [commentFile, setCommentFile] = useState<File | null>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const [error, setError] = useState('');
  const [current, setCurrent] = useState<ProjectTask | null>(task);
  const [mobilePanel, setMobilePanel] = useState<'details' | 'comments'>('details');
  const commentsScrollRef = useRef<HTMLDivElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const newCommentFileRef = useRef<HTMLInputElement>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string | null>(null);

  useEffect(() => {
    setCurrent(task);
  }, [task]);

  useEffect(() => {
    if (!focusCommentId) return;
    setMobilePanel('comments');
    const node = document.getElementById(`comment-${focusCommentId}`);
    node?.scrollIntoView({ block: 'center' });
  }, [focusCommentId, current?._id]);

  const columnOptions = project.columns
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((column) => ({ label: column.title, value: column.id }));

  const projectPeople = useMemo(() => {
    const byId = new Map<string, ProjectUser>();
    (project.members || []).forEach((person) => byId.set(person._id, person));
    if (project.createdBy?._id) byId.set(project.createdBy._id, project.createdBy);
    return Array.from(byId.values());
  }, [project.members, project.createdBy]);

  const otherAssignees = useMemo(
    () => projectPeople.filter((person) => person._id !== user?._id),
    [projectPeople, user?._id]
  );

  const otherTasks = useMemo(
    () => tasks.filter((item) => item._id !== task?._id),
    [tasks, task?._id]
  );

  const isNew = !current;
  const finishedColumnId = finishedColumnIdOf(project);
  const statusBlockedByDepends = !isNew && dependsOn.some((id) => {
    const item = tasks.find((entry) => entry._id === id);
    return !item || item.status !== finishedColumnId;
  });

  const snapshot = useCallback(
    () =>
      JSON.stringify({
        title: title.trim(),
        description,
        priority,
        status,
        completionPercent,
        dueDate: toIsoDay(dueDate),
        participants,
        dependsOn
      }),
    [title, description, priority, status, completionPercent, dueDate, participants, dependsOn]
  );

  const persist = useCallback(async () => {
    if (!current || !title.trim()) return;
    const next = snapshot();
    if (lastSavedRef.current === next) return;
    const updated = await projectService.updateTask(current._id, {
      title: title.trim(),
      description,
      priority,
      status,
      completionPercent,
      dueDate: toIsoDay(dueDate),
      participants,
      assignedTo: participants[0],
      dependsOn
    });
    lastSavedRef.current = next;
    setCurrent(updated);
    onSaved(updated);
    return updated;
  }, [current, title, description, priority, status, completionPercent, dueDate, participants, dependsOn, snapshot, onSaved]);

  useEffect(() => {
    if (!current) {
      lastSavedRef.current = null;
      return;
    }
    lastSavedRef.current = snapshot();
    // Only seed the baseline when the opened task changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?._id]);

  useEffect(() => {
    if (!current || !title.trim()) return;
    if (!lastSavedRef.current) return;
    const next = snapshot();
    if (next === lastSavedRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      persist().catch((err) => setError(err.response?.data?.message || 'Could not save task'));
    }, 600);
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [current, title, snapshot, persist]);

  const toggleParticipant = (userId: string) => {
    setParticipants((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };

  const save = async () => {
    if (!title.trim()) return;
    try {
      setSaving(true);
      setError('');
      const payload = {
        title: title.trim(),
        description,
        priority,
        status,
        completionPercent: isNew ? 0 : completionPercent,
        dueDate: toIsoDay(dueDate),
        participants: isNew && user?._id ? Array.from(new Set([user._id, ...participants])) : participants,
        dependsOn
      };
      if (!current) {
        const created = await projectService.createTask(project._id, payload as any);
        onSaved(created);
        onClose();
        return;
      }
      const updated = await persist();
      if (updated) onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not save task');
    } finally {
      setSaving(false);
    }
  };

  const closeSheet = async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    try {
      await persist();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not save task');
      return;
    }
    onClose();
  };

  const addComment = async () => {
    if (!current || (isEmptyHtml(comment) && !commentFile)) return;
    try {
      setCommentSaving(true);
      const updated = await projectService.addComment(current._id, comment.trim() || comment, commentFile || undefined);
      setCurrent(updated);
      setComment('');
      setCommentFile(null);
      onSaved(updated);
      requestAnimationFrame(() => {
        const el = commentsScrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not add comment');
    } finally {
      setCommentSaving(false);
    }
  };

  const uploadFile = async (file?: File | null) => {
    if (!current || !file) return;
    try {
      const updated = await projectService.uploadTaskFile(current._id, file);
      setCurrent(updated);
      onSaved(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not upload file');
    }
  };

  const mentionUsers = projectPeople.map((person) => ({
    id: person._id,
    name: personName(person),
    email: person.email
  }));

  if (!isNew) {
    const comments = current.comments || [];
    const dependableTasks = otherTasks.filter(
      (item) => !dependsOn.includes(item._id) && item.status !== finishedColumnId
    );

    return (
      <div className="app-modal-overlay task-sheet-overlay">
        <div className="task-sheet" onClick={(e) => e.stopPropagation()}>
          <div className="task-sheet-header">
            <h2>{title.trim() || 'Task'}</h2>
            <button type="button" className="app-modal-close" onClick={closeSheet} aria-label="Close">
              ×
            </button>
          </div>

          <div className="task-sheet-tabs">
            <button type="button" className={mobilePanel === 'details' ? 'is-active' : ''} onClick={() => setMobilePanel('details')}>
              Details
            </button>
            <button
              type="button"
              className={mobilePanel === 'comments' ? 'is-active' : ''}
              onClick={() => setMobilePanel('comments')}
            >
              Comments ({comments.length})
            </button>
          </div>

          {error && <p className="usage-error task-sheet-error">{error}</p>}

          <div className="task-sheet-body">
            <section className={`task-sheet-details${mobilePanel === 'details' ? ' is-open' : ''}`}>
              <div className="task-sheet-scroll">
                <div className="form-group">
                  <label htmlFor="task-name">Name</label>
                  <InputText id="task-name" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task name" className="task-sheet-name" />
                </div>
                <div className="form-group">
                  <label htmlFor="task-description">Description</label>
                  <InputTextarea
                    id="task-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    autoResize
                    placeholder="Task description"
                  />
                </div>

                <div className="task-sheet-grid-3">
                  <div className="form-group">
                    <label htmlFor="task-status">Status</label>
                    <select
                      id="task-status"
                      className="task-sheet-select"
                      value={status}
                      disabled={statusBlockedByDepends}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      {project.columns
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .map((column) => (
                          <option key={column.id} value={column.id}>
                            {column.title}
                          </option>
                        ))}
                    </select>
                    {statusBlockedByDepends && (
                      <p className="create-members-hint">Status is locked until every task listed under Depends on is done.</p>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="task-priority">Priority</label>
                    <select
                      id="task-priority"
                      className="task-sheet-select"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
                    >
                      {PRIORITY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="task-completion">Completion</label>
                    <select
                      id="task-completion"
                      className="task-sheet-select"
                      value={String(completionPercent)}
                      onChange={(e) => setCompletionPercent(Number(e.target.value))}
                    >
                      {COMPLETION_OPTIONS.map((pct) => (
                        <option key={pct} value={pct}>
                          {pct}%
                        </option>
                      ))}
                    </select>
                    <div className="task-completion-bar">
                      <span style={{ width: `${completionPercent}%` }} />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Due date</label>
                  <Calendar
                    value={dueDate}
                    onChange={(e) => setDueDate(e.value as Date | null)}
                    dateFormat="dd M yy"
                    placeholder="Select date"
                    showButtonBar
                    appendTo={document.body}
                    baseZIndex={3500}
                    className="w-full"
                    panelClassName="projects-datepicker"
                  />
                </div>

                <div className="task-sheet-grid-2">
                  <div className="form-group">
                    <label>Assigned</label>
                    <div className="task-assigned-list">
                      {projectPeople.length === 0 && <p className="stage-empty">No project members.</p>}
                      {projectPeople.map((person) => {
                        const selected = participants.includes(person._id);
                        return (
                          <button
                            key={person._id}
                            type="button"
                            className={`task-assigned-row${selected ? ' is-selected' : ''}`}
                            onClick={() => toggleParticipant(person._id)}
                          >
                            <span className={`create-member-check${selected ? ' is-checked' : ''}`} />
                            <span className="create-member-copy">{personName(person)}</span>
                            {selected && participants[0] === person._id && <span className="task-main-badge">Main</span>}
                          </button>
                        );
                      })}
                    </div>
                    <p className="create-members-hint">The first selected will be the main assignee. Click a row to select.</p>
                  </div>
                  <div className="form-group">
                    <label htmlFor="task-depends">Depends on</label>
                    <select
                      id="task-depends"
                      className="task-sheet-select"
                      value=""
                      onChange={(e) => {
                        const id = e.target.value;
                        if (id && !dependsOn.includes(id)) setDependsOn([...dependsOn, id]);
                      }}
                    >
                      <option value="">Add task...</option>
                      {dependableTasks.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.title}
                        </option>
                      ))}
                    </select>
                    <p className="create-members-hint">Tasks you must complete before this one.</p>
                    {dependsOn.length > 0 && (
                      <div className="task-depends-box">
                        {dependsOn.map((id) => {
                          const item = otherTasks.find((entry) => entry._id === id);
                          const done = item?.status === finishedColumnId;
                          return (
                            <div key={id} className="task-depends-row">
                              <span className={`task-depends-chip${done ? ' is-done' : ''}`}>{item?.title || id.slice(-6)}</span>
                              <button type="button" className="text-btn" onClick={() => setDependsOn(dependsOn.filter((entry) => entry !== id))} aria-label="Remove">
                                ×
                              </button>
                            </div>
                          );
                        })}
                        <p className="create-members-hint" style={{ marginBottom: 0 }}>
                          This task can only be done after the listed tasks are completed.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="task-sheet-section">
                  <h3>Attachments</h3>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    hidden
                    onChange={(e) => {
                      uploadFile(e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                  <button type="button" className="task-outline-btn" onClick={() => attachmentInputRef.current?.click()}>
                    Add file
                  </button>
                  <p className="create-members-hint">Files are stored on the server. Max 2 MB per file.</p>
                  {(current.attachments || []).length === 0 ? (
                    <p className="task-muted">No attachments yet.</p>
                  ) : (
                    <div className="task-file-list">
                      {(current.attachments || []).map((file) => (
                        <div key={file._id} className="task-file-card">
                          <strong>{file.originalName}</strong>
                          <span>
                            {formatFileSize(file.size)}
                            {file.uploadedBy ? ` · ${personName(file.uploadedBy)}` : ''}
                          </span>
                          <div className="task-file-actions">
                            <button type="button" className="task-outline-btn" onClick={() => projectService.downloadTaskFile(current._id, file._id, file.originalName)}>
                              Download
                            </button>
                            <button
                              type="button"
                              className="text-btn"
                              onClick={async () => {
                                const updated = await projectService.deleteTaskFile(current._id, file._id);
                                setCurrent(updated);
                                onSaved(updated);
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <details className="task-info">
                  <summary>Information</summary>
                  <div className="task-info-row">
                    <span>Created by:</span>
                    <strong>{personName(current.createdBy)}</strong>
                  </div>
                  <div className="task-info-row">
                    <span>Created:</span>
                    <strong>{formatDateTime(current.createdAt)}</strong>
                  </div>
                  {current.updatedAt && current.updatedAt !== current.createdAt && (
                    <div className="task-info-row">
                      <span>Modified:</span>
                      <strong>{formatDateTime(current.updatedAt)}</strong>
                    </div>
                  )}
                </details>
              </div>

              <div className="task-sheet-footer">
                <Button
                  label="Delete task"
                  className="p-button-danger"
                  onClick={async () => {
                    await projectService.deleteTask(current._id);
                    onDeleted(current._id);
                  }}
                />
                <Button label="Close" className="p-button-secondary" onClick={closeSheet} />
              </div>
            </section>

            <section className={`task-sheet-comments${mobilePanel === 'comments' ? ' is-open' : ''}`}>
              <div className="task-comments-head">
                <h3>Comments ({comments.length})</h3>
              </div>
              <div className="task-comments-scroll" ref={commentsScrollRef}>
                {comments.length === 0 ? (
                  <p className="task-comments-empty">No comments. Be the first to comment.</p>
                ) : (
                  comments.map((item) => {
                    const avatar = getAssetUrl(item.createdBy?.avatar);
                    const mentionsMe = user?._id && (item.text || '').includes(`data-user-id="${user._id}"`);
                    return (
                      <article
                        key={item._id}
                        id={`comment-${item._id}`}
                        className={`task-comment${focusCommentId === item._id ? ' is-focused' : ''}${mentionsMe ? ' is-mention' : ''}`}
                      >
                        <div className="task-comment-head">
                          {avatar ? (
                            <img src={avatar} alt="" className="list-avatar" />
                          ) : (
                            <span className="list-avatar list-avatar-fallback">{initials(item.createdBy)}</span>
                          )}
                          <div>
                            <strong>{personName(item.createdBy)}</strong>
                            <span>
                              {formatDateTime(item.createdAt)}
                              {(item.versions?.length || 0) > 0 ||
                              (item.updatedAt && new Date(item.updatedAt).getTime() > new Date(item.createdAt).getTime())
                                ? ' · edited'
                                : ''}
                            </span>
                          </div>
                          {item.createdBy?._id === user?._id && (
                            <button
                              type="button"
                              className="text-btn"
                              onClick={async () => {
                                const updated = await projectService.deleteComment(current._id, item._id);
                                setCurrent(updated);
                                onSaved(updated);
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                        <div className="comment-html-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.text) }} />
                        {(item.attachments || []).length > 0 && (
                          <div className="task-comment-files">
                            {(item.attachments || []).map((file) => (
                              <div key={file._id} className="task-file-card is-compact">
                                <strong>{file.originalName}</strong>
                                <span>{formatFileSize(file.size)}</span>
                                <button
                                  type="button"
                                  className="task-outline-btn"
                                  onClick={() => projectService.downloadCommentFile(current._id, item._id, file._id, file.originalName)}
                                >
                                  Download
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </article>
                    );
                  })
                )}
              </div>
              <div className="task-comment-composer">
                <label>Add comment (Ctrl+B bold, Ctrl+I italic, Ctrl+U underline, Ctrl+K link)</label>
                <CommentRichEditor
                  value={comment}
                  onChange={setComment}
                  placeholder="Write a comment... Use @ to mention someone"
                  disabled={commentSaving}
                  mentionUsers={mentionUsers}
                />
                <input
                  ref={newCommentFileRef}
                  type="file"
                  hidden
                  onChange={(e) => {
                    setCommentFile(e.target.files?.[0] || null);
                    e.target.value = '';
                  }}
                />
                <div className="task-comment-composer-actions">
                  <button type="button" className="text-btn" onClick={() => newCommentFileRef.current?.click()}>
                    Attach file
                  </button>
                  {commentFile && (
                    <>
                      <span className="task-muted">{commentFile.name}</span>
                      <button type="button" className="text-btn" onClick={() => setCommentFile(null)}>
                        Clear
                      </button>
                    </>
                  )}
                  <span className="task-muted">Max 2 MB</span>
                </div>
                <Button
                  label={commentSaving ? 'Adding…' : 'Add comment'}
                  onClick={addComment}
                  disabled={commentSaving || (isEmptyHtml(comment) && !commentFile)}
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="app-modal-overlay"
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.p-datepicker, .p-dropdown-panel, .p-multiselect-panel')) return;
        onClose();
      }}
    >
      <div className="app-modal task-modal" onClick={(e) => e.stopPropagation()}>
        <div className="app-modal-header">
          <div className="app-modal-header-text">
            <h2>Add task</h2>
            <p>Add a task to this project</p>
          </div>
          <button type="button" className="app-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-form">
          {error && <p className="usage-error">{error}</p>}

          <div className="form-group">
            <label htmlFor="task-title">Title</label>
            <InputText
              id="task-title"
              value={title}
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to be done?"
              onKeyDown={(e) => {
                if (e.key === 'Enter') save();
              }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="new-task-description">Description</label>
            <InputTextarea
              id="new-task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              autoResize
              placeholder="Optional details"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <Dropdown
                value={status}
                options={columnOptions}
                onChange={(e) => setStatus(e.value)}
                className="w-full"
                appendTo={document.body}
                panelClassName="projects-dropdown-panel"
              />
            </div>
            <div className="form-group">
              <label>Priority</label>
              <Dropdown
                value={priority}
                options={PRIORITY_OPTIONS}
                onChange={(e) => setPriority(e.value)}
                className="w-full"
                appendTo={document.body}
                panelClassName="projects-dropdown-panel"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Due date</label>
            <Calendar
              value={dueDate}
              onChange={(e) => setDueDate(e.value as Date | null)}
              dateFormat="dd M yy"
              placeholder="Select date"
              showButtonBar
              appendTo={document.body}
              baseZIndex={3500}
              className="w-full"
              panelClassName="projects-datepicker"
            />
          </div>
          <div className="form-group">
            <label>Assignees</label>
            <p className="create-members-hint">You are assigned automatically. Add others if needed.</p>
            <div className="create-member-list">
              {otherAssignees.length === 0 && (
                <p className="stage-empty" style={{ borderTop: 'none', padding: '0.5rem 0' }}>
                  No other project members
                </p>
              )}
              {otherAssignees.map((person) => {
                const selected = participants.includes(person._id);
                return (
                  <button
                    key={person._id}
                    type="button"
                    className={`create-member-row${selected ? ' is-selected' : ''}`}
                    onClick={() => toggleParticipant(person._id)}
                  >
                    <span className={`create-member-check${selected ? ' is-checked' : ''}`} />
                    <span className="create-member-copy">{personName(person)}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {otherTasks.length > 0 && (
            <div className="form-group">
              <label>Depends on</label>
              <MultiSelect
                value={dependsOn}
                options={otherTasks.map((item) => ({ label: item.title, value: item._id }))}
                onChange={(e) => setDependsOn(e.value || [])}
                placeholder="Select tasks"
                display="chip"
                filter
                className="w-full"
                appendTo={document.body}
                panelClassName="projects-multiselect-panel"
              />
            </div>
          )}

          <div className="modal-actions">
            <Button label="Cancel" className="p-button-secondary" onClick={onClose} />
            <Button label={saving ? 'Adding…' : 'Add task'} onClick={save} disabled={saving || !title.trim()} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectTaskModal;
